import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ShoutPlusBriefService } from '@/app/shout-plus/services/shout-plus-brief/shout-plus-brief.service';
import {
  CreateBriefPaymentMethodDto,
  VoucherCodeRow,
  VoucherCodeSummary,
  VoucherSalesCreatorRow,
  VoucherSalesSummary,
} from '@/app/shout-plus/models/shout-plus-brief';
import { parseVoucherCodes, voucherCodeLinesFromFile } from '@/app/shout-plus/utils/voucher-codes.util';
import { parseOrderExport } from '@/app/shout-plus/utils/order-export.util';
import { ImageService } from '@/app/services/image/image.service';

const PAGE_SIZE = 25;
/** Warn the brand when this few codes are left. */
const LOW_STOCK = 5;

/**
 * SHOUT+ voucher reward on the brief page. Unique-code briefs get the codes
 * table (who got which code), counts, adding more codes, removing unused ones,
 * and sales by creator from the brand's imported order exports; shared-code
 * briefs just show the code.
 */
@Component({
  selector: 'mbt-briefing-voucher-codes',
  imports: [DatePipe],
  templateUrl: './briefing-voucher-codes.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingVoucherCodes {
  readonly paymentMethodId = input.required<string>();

  private readonly shoutPlusBriefService = inject(ShoutPlusBriefService);
  readonly imageService = inject(ImageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly method = signal<CreateBriefPaymentMethodDto | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal(false);

  readonly summary = signal<VoucherCodeSummary | null>(null);
  readonly rows = signal<VoucherCodeRow[]>([]);
  readonly total = signal(0);
  readonly search = signal('');
  readonly loadingRows = signal(false);

  readonly adding = signal(false);
  readonly addText = signal('');
  readonly saving = signal(false);
  readonly removingId = signal<string | null>(null);
  readonly message = signal<{ kind: 'success' | 'error'; text: string } | null>(null);

  readonly sales = signal<VoucherSalesSummary | null>(null);
  readonly importing = signal(false);
  readonly salesMessage = signal<{ kind: 'success' | 'error'; text: string } | null>(null);

  readonly isUnique = computed(() => !!this.method()?.unique_codes);
  /** Codes are created in the brand's Shopify store as creators complete, so there's no stock to run out. */
  readonly isShopify = computed(() => this.isUnique() && !!this.method()?.shopify_voucher_codes);
  readonly pendingCodes = computed(() => parseVoucherCodes(this.addText()));
  readonly hasMore = computed(() => this.rows().length < this.total());
  readonly stockWarning = computed(() => {
    if (this.isShopify()) {
      return null;
    }
    const summary = this.summary();
    if (!summary || summary.total === 0) {
      return null;
    }
    if (summary.remaining === 0) {
      return 'No codes left. Creators who complete the brief now won\'t get one until you add more.';
    }
    if (summary.remaining <= LOW_STOCK) {
      return `Only ${summary.remaining} ${summary.remaining === 1 ? 'code' : 'codes'} left. Add more so every creator who completes gets one.`;
    }
    return null;
  });

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const id = this.paymentMethodId();
      void this.load(id);
    });
    this.destroyRef.onDestroy(() => {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
    });
  }

  private async load(id: string): Promise<void> {
    this.loading.set(true);
    this.loadError.set(false);
    try {
      const method = await firstValueFrom(this.shoutPlusBriefService.getShoutPlusPaymentMethod(id));
      this.method.set(method);
      if (method?.unique_codes) {
        await Promise.all([this.refreshSummary(), this.loadRows(true), this.refreshSales()]);
      }
    } catch {
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  private async refreshSummary(): Promise<void> {
    this.summary.set(await firstValueFrom(this.shoutPlusBriefService.getVoucherCodeSummary(this.paymentMethodId())));
  }

  async loadRows(reset: boolean): Promise<void> {
    this.loadingRows.set(true);
    try {
      const page = await firstValueFrom(
        this.shoutPlusBriefService.listVoucherCodes(this.paymentMethodId(), {
          search: this.search(),
          skip: reset ? 0 : this.rows().length,
          take: PAGE_SIZE,
        }),
      );
      this.rows.set(reset ? page.items : [...this.rows(), ...page.items]);
      this.total.set(page.total);
    } catch {
      this.message.set({ kind: 'error', text: 'We couldn\'t load your codes. Please try again.' });
    } finally {
      this.loadingRows.set(false);
    }
  }

  onSearchInput(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => void this.loadRows(true), 300);
  }

  // ---- Add codes -----------------------------------------------------------

  toggleAdding(): void {
    this.adding.update((open) => !open);
    this.message.set(null);
  }

  onAddInput(event: Event): void {
    this.addText.set((event.target as HTMLTextAreaElement).value);
  }

  async onAddFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    try {
      const lines = voucherCodeLinesFromFile(await file.text());
      this.addText.set([this.addText().trim(), ...lines].filter(Boolean).join('\n'));
    } catch {
      this.message.set({ kind: 'error', text: 'We couldn\'t read that file. Try a .csv or .txt file with one code per line.' });
    }
  }

  async saveCodes(): Promise<void> {
    const codes = this.pendingCodes();
    if (!codes.length || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.message.set(null);
    try {
      const result = await firstValueFrom(this.shoutPlusBriefService.addVoucherCodes(this.paymentMethodId(), codes));
      const skipped = codes.length - result.added;
      this.summary.set({ total: result.total, assigned: result.assigned, remaining: result.remaining });
      this.addText.set('');
      this.adding.set(false);
      this.message.set({
        kind: 'success',
        text: `Added ${result.added} ${result.added === 1 ? 'code' : 'codes'}`
          + (skipped > 0 ? ` (${skipped} already in your list)` : '') + '.',
      });
      await this.loadRows(true);
    } catch {
      this.message.set({ kind: 'error', text: 'We couldn\'t add those codes. Please check them and try again.' });
    } finally {
      this.saving.set(false);
    }
  }

  // ---- Remove --------------------------------------------------------------

  async removeCode(row: VoucherCodeRow): Promise<void> {
    if (row.status !== 'available' || this.removingId()) {
      return;
    }
    this.removingId.set(row.id);
    this.message.set(null);
    try {
      this.summary.set(await firstValueFrom(this.shoutPlusBriefService.removeVoucherCode(this.paymentMethodId(), row.id)));
      this.rows.update((rows) => rows.filter((item) => item.id !== row.id));
      this.total.update((total) => Math.max(total - 1, 0));
      this.message.set({ kind: 'success', text: `Removed ${row.code}.` });
    } catch {
      this.message.set({ kind: 'error', text: `${row.code} couldn't be removed. It may have just been given to a creator.` });
      await Promise.all([this.refreshSummary(), this.loadRows(true)]);
    } finally {
      this.removingId.set(null);
    }
  }

  // ---- Sales ---------------------------------------------------------------

  private async refreshSales(): Promise<void> {
    try {
      this.sales.set(await firstValueFrom(this.shoutPlusBriefService.getVoucherSalesSummary(this.paymentMethodId())));
    } catch {
      this.sales.set(null);
    }
  }

  async onImportOrders(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.importing()) {
      return;
    }
    this.salesMessage.set(null);
    let parsed: ReturnType<typeof parseOrderExport>;
    try {
      parsed = parseOrderExport(await file.text());
    } catch {
      this.salesMessage.set({ kind: 'error', text: 'We couldn\'t read that file. Export your orders as a CSV and try again.' });
      return;
    }
    if (parsed.missing.length) {
      this.salesMessage.set({
        kind: 'error',
        text: `We couldn't find the ${parsed.missing.join(', ')} column${parsed.missing.length === 1 ? '' : 's'} in that file. Use your store's order export.`,
      });
      return;
    }
    if (!parsed.rows.length) {
      this.salesMessage.set({ kind: 'error', text: 'That file has no orders in it.' });
      return;
    }

    this.importing.set(true);
    try {
      const result = await firstValueFrom(this.shoutPlusBriefService.importVoucherSales(this.paymentMethodId(), parsed.rows));
      const notes = [
        result.alreadyImported ? `${result.alreadyImported} already imported` : '',
        result.notGivenOut ? `${result.notGivenOut} used a code not yet given to a creator` : '',
        result.unmatched + result.withoutCode ? `${result.unmatched + result.withoutCode} didn't use these codes` : '',
      ].filter(Boolean);
      this.salesMessage.set({
        kind: 'success',
        text: `Imported ${result.imported} ${result.imported === 1 ? 'order' : 'orders'}` + (notes.length ? ` (${notes.join(', ')}).` : '.'),
      });
      await this.refreshSales();
    } catch {
      this.salesMessage.set({ kind: 'error', text: 'We couldn\'t import those orders. Please try again.' });
    } finally {
      this.importing.set(false);
    }
  }

  formatMoney(amount: number): string {
    const currency = this.sales()?.currency || 'GBP';
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  salesCreatorName(row: VoucherSalesCreatorRow): string {
    return [row.creator.firstName, row.creator.lastName].filter(Boolean).join(' ') || 'Creator';
  }

  salesCreatorInitials(row: VoucherSalesCreatorRow): string {
    return `${row.creator.firstName?.charAt(0) ?? ''}${row.creator.lastName?.charAt(0) ?? ''}`.toUpperCase() || '?';
  }

  creatorName(row: VoucherCodeRow): string {
    const creator = row.creator;
    return creator ? [creator.firstName, creator.lastName].filter(Boolean).join(' ') || 'Creator' : '';
  }

  creatorInitials(row: VoucherCodeRow): string {
    const creator = row.creator;
    return `${creator?.firstName?.charAt(0) ?? ''}${creator?.lastName?.charAt(0) ?? ''}`.toUpperCase() || '?';
  }
}
